import {
  CssVariableType,
  OmakaseColorTokens,
  omakaseColorTokensHighContrast,
  omakaseColorTokensLight,
} from 'theme/themeCssVariables';
import { Interpolation, Theme } from '@emotion/react';

export type OmakaseColorTheme = 'light' | 'highContrast';

export type ScopedOverride = (colorThemeType: OmakaseColorTheme) => Interpolation<Theme>;

export const getTokensForColorThemeType = (colorThemeType: OmakaseColorTheme) => {
  switch (colorThemeType) {
    case 'light':
      return omakaseColorTokensLight;
    case 'highContrast':
      return omakaseColorTokensHighContrast;
  }
};

export const scopedOverride = (themeType: OmakaseColorTheme) => {
  const tokens = getTokensForColorThemeType(themeType);
  return Object.keys(tokens).map((key) => {
    return `${key}: ${tokens[key as keyof typeof tokens]}`;
  });
};

export interface OmakaseColors {
  // Backgrounds
  bgDefault: CssVariableType<OmakaseColorTokens>;
  bgLighter: CssVariableType<OmakaseColorTokens>;
  bgLight: CssVariableType<OmakaseColorTokens>;
  bgDark: CssVariableType<OmakaseColorTokens>;
  bgDarker: CssVariableType<OmakaseColorTokens>;
  bgContrast: CssVariableType<OmakaseColorTokens>;
  bgOverlay: CssVariableType<OmakaseColorTokens>;
  bgPrimaryLighter: CssVariableType<OmakaseColorTokens>;
  bgPrimaryLight: CssVariableType<OmakaseColorTokens>;
  bgPrimaryBase: CssVariableType<OmakaseColorTokens>;
  bgPrimaryDark: CssVariableType<OmakaseColorTokens>;
  bgSuccessLighter: CssVariableType<OmakaseColorTokens>;
  bgSuccessBase: CssVariableType<OmakaseColorTokens>;
  bgWarningLighter: CssVariableType<OmakaseColorTokens>;
  bgWarningBase: CssVariableType<OmakaseColorTokens>;
  bgDangerLighter: CssVariableType<OmakaseColorTokens>;
  bgDangerBase: CssVariableType<OmakaseColorTokens>;

  // Borders
  borderDefault: CssVariableType<OmakaseColorTokens>;
  borderDark: CssVariableType<OmakaseColorTokens>;
  borderContrast: CssVariableType<OmakaseColorTokens>;
  borderPrimaryLight: CssVariableType<OmakaseColorTokens>;
  borderPrimaryBase: CssVariableType<OmakaseColorTokens>;
  borderPrimaryDark: CssVariableType<OmakaseColorTokens>;
  borderSuccessLight: CssVariableType<OmakaseColorTokens>;
  borderSuccessBase: CssVariableType<OmakaseColorTokens>;
  borderWarningLight: CssVariableType<OmakaseColorTokens>;
  borderWarningBase: CssVariableType<OmakaseColorTokens>;
  borderDangerLight: CssVariableType<OmakaseColorTokens>;
  borderDangerBase: CssVariableType<OmakaseColorTokens>;

  // Text
  fgDefault: CssVariableType<OmakaseColorTokens>;
  fgSubtle: CssVariableType<OmakaseColorTokens>;
  fgDisabled: CssVariableType<OmakaseColorTokens>;
  fgContrast: CssVariableType<OmakaseColorTokens>;
  fgPrimaryBase: CssVariableType<OmakaseColorTokens>;
  fgPrimaryDark: CssVariableType<OmakaseColorTokens>;
  fgDangerBase: CssVariableType<OmakaseColorTokens>;
  fgSuccessBase: CssVariableType<OmakaseColorTokens>;
  fgWarningBase: CssVariableType<OmakaseColorTokens>;
}

export interface OmakaseLegacyDecorativeColors {
  legacyDecorative02: CssVariableType<OmakaseColorTokens>;
  legacyDecorative03: CssVariableType<OmakaseColorTokens>;
  legacyDecorative04: CssVariableType<OmakaseColorTokens>;
  legacyDecorative05: CssVariableType<OmakaseColorTokens>;
  legacyDecorative06: CssVariableType<OmakaseColorTokens>;
  legacyDecorative07: CssVariableType<OmakaseColorTokens>;
  legacyDecorative08: CssVariableType<OmakaseColorTokens>;
  legacyDecorative09: CssVariableType<OmakaseColorTokens>;
  legacyDecorative10: CssVariableType<OmakaseColorTokens>;
  legacyDecorative11: CssVariableType<OmakaseColorTokens>;
  legacyDecorative12: CssVariableType<OmakaseColorTokens>;
  legacyDecorative13: CssVariableType<OmakaseColorTokens>;
  legacyDecorative14: CssVariableType<OmakaseColorTokens>;
  legacyDecorative15: CssVariableType<OmakaseColorTokens>;
  legacyDecorative16: CssVariableType<OmakaseColorTokens>;
  legacyDecorative17: CssVariableType<OmakaseColorTokens>;
  legacyDecorative18: CssVariableType<OmakaseColorTokens>;
  legacyDecorative19: CssVariableType<OmakaseColorTokens>;
}

/**
 * OmakaseColorsStringType allows for casting omakaseLightColors to string type for integration with MUI component style props
 */
export interface OmakaseColorsStringType {
  // Backgrounds
  bgDefault: string;
  bgLighter: string;
  bgLight: string;
  bgDark: string;
  bgDarker: string;
  bgContrast: string;
  bgOverlay: string;
  bgPrimaryLighter: string;
  bgPrimaryLight: string;
  bgPrimaryBase: string;
  bgPrimaryDark: string;
  bgSuccessLighter: string;
  bgSuccessBase: string;
  bgWarningLighter: string;
  bgWarningBase: string;
  bgDangerLighter: string;
  bgDangerBase: string;

  // Borders
  borderDefault: string;
  borderDark: string;
  borderContrast: string;
  borderPrimaryLight: string;
  borderPrimaryBase: string;
  borderSuccessLight: string;
  borderPrimaryDark: string;
  borderSuccessBase: string;
  borderWarningLight: string;
  borderWarningBase: string;
  borderDangerLight: string;
  borderDangerBase: string;

  // Text
  fgDefault: string;
  fgSubtle: string;
  fgDisabled: string;
  fgContrast: string;
  fgPrimaryBase: string;
  fgPrimaryDark: string;
  fgDangerBase: string;
  fgSuccessBase: string;
  fgWarningBase: string;
}

export type OmakaseColorsLegacyDecorative = {
  legacyDecorative02: string;
  legacyDecorative03: string;
  legacyDecorative04: string;
  legacyDecorative05: string;
  legacyDecorative06: string;
  legacyDecorative07: string;
  legacyDecorative08: string;
  legacyDecorative09: string;
  legacyDecorative10: string;
  legacyDecorative11: string;
  legacyDecorative12: string;
  legacyDecorative13: string;
  legacyDecorative14: string;
  legacyDecorative15: string;
  legacyDecorative16: string;
  legacyDecorative17: string;
  legacyDecorative18: string;
  legacyDecorative19: string;
};

export const omakaseLightColors: OmakaseColors = {
  // Backgrounds
  bgDefault: 'var(--bg-default, #fff)',
  bgLighter: 'var(--bg-lighter, #f9f9f9)',
  bgLight: 'var(--bg-light, #f6f6f7)',
  bgDark: 'var(--bg-dark, #484848)',
  bgDarker: 'var(--bg-darker, #2b2b2b)',
  bgContrast: 'var(--bg-contrast, #000000)',
  bgOverlay: 'var(--bg-overlay, rgba(0, 0, 0, 0.5))',
  bgPrimaryLighter: 'var(--bg-primary-lighter, #f1faff)',
  bgPrimaryLight: 'var(--bg-primary-light, #d0e2ff)',
  bgPrimaryBase: 'var(--bg-primary-base, #344ef4)',
  bgPrimaryDark: 'var(--bg-primary-dark, #2a2ae9)',
  bgSuccessLighter: 'var(--bg-success-lighter, #f1f8f5)',
  bgSuccessBase: 'var(--bg-success-base, #008060)',
  bgDangerLighter: 'var(--bg-danger-lighter, #fff4f4)',
  bgDangerBase: 'var(--bg-danger-base, #da1e28)',
  bgWarningLighter: 'var(--bg-warning-lighter, #fff5ea)',
  bgWarningBase: 'var(--bg-warning-base, #916a00)',

  // Borders
  borderDefault: 'var(--border-default, #e7e7e7)',
  borderDark: 'var(--border-dark, #2b2b2b)',
  borderContrast: 'var(--border-contrast, #ffffff)',
  borderPrimaryLight: 'var(--border-primary-light, #abddff)',
  borderPrimaryBase: 'var(--border-primary-base, #344ef4)',
  borderPrimaryDark: 'var(--border-primary-dark, #2a2ae9)',
  borderSuccessLight: 'var(--border-success-light, #aee9d1)',
  borderSuccessBase: 'var(--border-success-base, #008060)',
  borderWarningLight: 'var(--border-warning-light, #ffd79d)',
  borderWarningBase: 'var(--border-warning-base, #916a00)',
  borderDangerLight: 'var(--border-danger-light, #ffbbbf)',
  borderDangerBase: 'var(--border-danger-base, #da1e28)',

  // Text
  fgDefault: 'var(--fg-default, #2b2b2b)',
  fgSubtle: 'var(--fg-subtle, #6f6f6f)',
  fgDisabled: 'var(--fg-disabled, #8c9196)',
  fgContrast: 'var(--fg-contrast, #fff)',
  fgPrimaryBase: 'var(--fg-primary-base, #344ef4)',
  fgPrimaryDark: 'var(--fg-primary-dark, #2a2ae9)',
  fgDangerBase: 'var(--fg-danger-base, #da1e28)',
  fgSuccessBase: 'var(--fg-success-base, #008060)',
  fgWarningBase: 'var(--fg-warning-base, #916a00)',
};

export const omakaseLegacyDecorativeColors: OmakaseColorsLegacyDecorative = {
  legacyDecorative02: 'var(--decorative-02, #edb815)',
  legacyDecorative03: 'var(--decorative-03, #af6acd)',
  legacyDecorative04: 'var(--decorative-04, #ff4b4b)',
  legacyDecorative05: 'var(--decorative-05, #ffd53e)',
  legacyDecorative06: 'var(--decorative-06, #01ad9d)',
  legacyDecorative07: 'var(--decorative-07, #e17474)',
  legacyDecorative08: 'var(--decorative-08, #bd2333)',
  legacyDecorative09: 'var(--decorative-09, #77624e)',
  legacyDecorative10: 'var(--decorative-10, #d684d8)',
  legacyDecorative11: 'var(--decorative-11, #a8535d)',
  legacyDecorative12: 'var(--decorative-12, #c69928)',
  legacyDecorative13: 'var(--decorative-13, #8e44ad)',
  legacyDecorative14: 'var(--decorative-14, #eb8a54)',
  legacyDecorative15: 'var(--decorative-15, #87bb6e)',
  legacyDecorative16: 'var(--decorative-16, #4368c8)',
  legacyDecorative17: 'var(--decorative-17, #3f90cb)',
  legacyDecorative18: 'var(--decorative-18, #7b7b8b)',
  legacyDecorative19: 'var(--decorative-19, #fabf37)',
};

export interface MuiPaletteColors {
  MuiPalettePrimary: string;
  MuiPaletteSecondary: string;
  MuiPaletteError: string;
  MuiPaletteSuccess: string;
  MuiPaletteInfo: string;
  MuiPaletteText: string;
  MuiPaletteHover: string;
}

export const muiPaletteColors: MuiPaletteColors = {
  MuiPalettePrimary: '#344ef4',
  MuiPaletteSecondary: '#506482',
  MuiPaletteError: '#df3232',
  MuiPaletteSuccess: '#3d8459',
  MuiPaletteInfo: '#e9e9f9',
  MuiPaletteText: '#2b2b2b',
  MuiPaletteHover: '#e9e9f9',
};
